import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../supabase/supabaseClient";
import "../style/SingleCar.css";
import Header from "../components/Header";

export default function SingleCar() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const fetchCar = async () => {
      try {
        setLoading(true);

        const { data, error } = await supabase
          .from("cars")
          .select("*")
          .eq("id", id)
          .single();

        if (error) throw error;

        setCar(data);
      } catch (error) {
        console.error("Error fetching car:", error);
        setCar(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCar();
  }, [id]);

  useEffect(() => {
    setSelectedImage(0);
  }, [id]);

  const images = useMemo(() => {
    if (!car) return [];

    if (Array.isArray(car.images) && car.images.length > 0) return car.images;
    if (Array.isArray(car.photos) && car.photos.length > 0) return car.photos;
    if (car.main_image) return [car.main_image];
    if (car.image_url) return [car.image_url];
    if (car.image) return [car.image];

    return [];
  }, [car]);

  const technicalEntries = useMemo(() => {
    if (!car?.technical_data) return [];
    return Object.entries(car.technical_data);
  }, [car]);

  const equipment = useMemo(() => {
    if (!Array.isArray(car?.equipment)) return [];
    return car.equipment;
  }, [car]);

  const specValue = (keys = []) => {
    if (!car?.technical_data) return "-";

    for (const key of keys) {
      const value = car.technical_data[key];

      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return value;
      }
    }

    return "-";
  };

  const nextImage = () => {
    if (!images.length) return;
    setSelectedImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    if (!images.length) return;
    setSelectedImage((prev) => (prev - 1 + images.length) % images.length);
  };

  if (loading) {
    return (
      <div className="single-car-page">
        <Header />
        <div className="single-car-loading">{t("singleCar.loading")}</div>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="single-car-page">
        <Header />
        <div className="single-car-loading">{t("singleCar.notFound")}</div>
      </div>
    );
  }

  const emailSubject = `${t("singleCar.emailSubject")} ${car.car_name}`;
  const emailBody = `${t("singleCar.emailHello")}

${t("singleCar.emailInterested")} "${car.car_name}".

${t("singleCar.emailMoreInfo")}

${t("singleCar.emailThanks")}`;

  return (
    <div className="single-car-page">
      <Header />

      <section
        className="single-hero"
        style={{
          backgroundImage: `linear-gradient(
            rgba(0, 0, 0, 0.62),
            rgba(0, 0, 0, 0.72)
          ), url(${images[0] || ""})`,
        }}
      >
        <div className="single-hero-content">
          <p className="single-breadcrumb">
            {t("singleCar.home")} / {t("singleCar.cars")} / {car.car_name}
          </p>

          <h1>{car.car_name || t("singleCar.carName")}</h1>

          <p className="single-hero-subtitle">
            {car.brand || t("singleCar.brand")} {car.year ? `• ${car.year}` : ""}
          </p>

          <div className="single-hero-specs">
            <div className="hero-spec">
              <span>{t("singleCar.mileage")}</span>
              <strong>{specValue(["Mileage", "mileage"])}</strong>
            </div>

            <div className="hero-spec">
              <span>{t("singleCar.fuel")}</span>
              <strong>{specValue(["Fuel type", "fuelType"])}</strong>
            </div>

            <div className="hero-spec">
              <span>{t("singleCar.transmission")}</span>
              <strong>{specValue(["Transmission", "transmission"])}</strong>
            </div>

            <div className="hero-spec">
              <span>{t("singleCar.power")}</span>
              <strong>{specValue(["Performance", "performance"])}</strong>
            </div>
          </div>

          <div className="single-hero-bottom">
            <button
              className="single-hero-back"
              onClick={() => navigate(-1)}
              type="button"
            >
              ← {t("singleCar.backToCars")}
            </button>

            <div className="single-hero-price">
              €{Number(car.price || 0).toLocaleString()}
            </div>
          </div>
        </div>
      </section>

      <div className="single-car-container">
        <div className="single-top-layout">
          <div className="single-main-column">
            <section className="single-gallery-card">
              <div className="single-gallery-main">
                {images.length > 0 ? (
                  <img
                    src={images[selectedImage]}
                    alt={car.car_name}
                    className="single-gallery-image"
                  />
                ) : (
                  <div className="single-gallery-placeholder">
                    {t("singleCar.noImage")}
                  </div>
                )}

                <div className="single-gallery-count">
                  {images.length > 0
                    ? `${selectedImage + 1} / ${images.length}`
                    : "0 / 0"}
                </div>

                {images.length > 1 && (
                  <>
                    <button
                      className="single-gallery-nav single-gallery-prev"
                      onClick={prevImage}
                      type="button"
                    >
                      ‹
                    </button>

                    <button
                      className="single-gallery-nav single-gallery-next"
                      onClick={nextImage}
                      type="button"
                    >
                      ›
                    </button>
                  </>
                )}
              </div>

              {images.length > 0 && (
                <div className="single-thumbs">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      className={`single-thumb ${
                        selectedImage === index ? "active" : ""
                      }`}
                      onClick={() => setSelectedImage(index)}
                      type="button"
                    >
                      <img
                        src={img}
                        alt={`${t("singleCar.thumbnail")} ${index + 1}`}
                      />
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section className="single-overview-card">
              <div className="single-overview-grid">
                <div className="single-overview-item">
                  <div className="single-overview-icon">≣</div>
                  <div>
                    <p>{t("singleCar.mileage")}</p>
                    <h4>{specValue(["Mileage", "mileage"])}</h4>
                  </div>
                </div>

                <div className="single-overview-item">
                  <div className="single-overview-icon">◔</div>
                  <div>
                    <p>{t("singleCar.performance")}</p>
                    <h4>{specValue(["Performance", "performance"])}</h4>
                  </div>
                </div>

                <div className="single-overview-item">
                  <div className="single-overview-icon">⛽</div>
                  <div>
                    <p>{t("singleCar.fuelType")}</p>
                    <h4>{specValue(["Fuel type", "fuelType"])}</h4>
                  </div>
                </div>

                <div className="single-overview-item">
                  <div className="single-overview-icon">⚙</div>
                  <div>
                    <p>{t("singleCar.transmission")}</p>
                    <h4>{specValue(["Transmission", "transmission"])}</h4>
                  </div>
                </div>

                <div className="single-overview-item">
                  <div className="single-overview-icon">◫</div>
                  <div>
                    <p>{t("singleCar.firstRegistration")}</p>
                    <h4>
                      {specValue(["First registration", "firstRegistration"])}
                    </h4>
                  </div>
                </div>

                <div className="single-overview-item">
                  <div className="single-overview-icon">◉</div>
                  <div>
                    <p>{t("singleCar.vehicleOwner")}</p>
                    <h4>
                      {specValue([
                        "Vehicle owner",
                        "owners",
                        "Number of owners of the vehicle",
                      ])}
                    </h4>
                  </div>
                </div>
              </div>
            </section>

            <section className="single-section-card">
              <h2>{t("singleCar.technicalData")}</h2>

              <div className="single-tech-table">
                {technicalEntries.length > 0 ? (
                  technicalEntries.map(([key, value], index) => (
                    <div
                      key={key}
                      className={`single-tech-row ${
                        index % 2 === 1 ? "alt" : ""
                      }`}
                    >
                      <div className="single-tech-key">{key}</div>
                      <div className="single-tech-value">{String(value)}</div>
                    </div>
                  ))
                ) : (
                  <p className="single-empty-text">
                    {t("singleCar.noTechnicalData")}
                  </p>
                )}
              </div>
            </section>

            <section className="single-section-card">
              <h2>{t("singleCar.equipment")}</h2>

              {equipment.length > 0 ? (
                <div className="single-equipment-grid">
                  {equipment.map((item, index) => (
                    <div
                      key={`${item}-${index}`}
                      className={`single-equipment-item ${
                        index % 2 === 1 ? "alt" : ""
                      }`}
                    >
                      <span>{item}</span>
                      <span className="single-equipment-check">✓</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="single-empty-text">
                  {t("singleCar.noEquipment")}
                </p>
              )}
            </section>

            <section className="single-section-card">
              <h2>{t("singleCar.vehicleDescription")}</h2>

              <div className="single-description">
                <p>{car.description || t("singleCar.noDescription")}</p>
              </div>
            </section>
          </div>

          <aside className="single-side-column">
            <div className="single-price-card">
              <h1>{car.car_name || t("singleCar.carName")}</h1>

              <p className="single-subtitle">
                {car.brand || ""}
                {car.brand && car.year ? " • " : ""}
                {car.year || ""}
              </p>

              <div className="single-price">
                €{Number(car.price || 0).toLocaleString()}
              </div>

              <div className="single-divider"></div>

              <div className="single-contact-row">
                <strong>{t("singleCar.tel")}: +49 175 1932947</strong>
              </div>

              <button
                className="single-email-btn"
                type="button"
                onClick={() => {
                  const companyEmail = "autopark.lennestadt@gmail.com";

                  const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
                    companyEmail
                  )}&su=${encodeURIComponent(
                    emailSubject
                  )}&body=${encodeURIComponent(emailBody)}`;

                  window.open(gmailUrl, "_blank");
                }}
              >
                ✉ {t("singleCar.writeEmail")}
              </button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}